import AddressForm from '@Components/AddressForm';
import { StyledCheckLabel } from '@Components/AddressForm/AddressForm.styled';
import { CheckBoxWrapper, CheckInput } from '@Components/common/Checkbox';
import AddressCard from '@Components/ProfilePage/MyAddresses/AddressCard';
import { AddNew, AddNewIcon } from '@Components/ProfilePage/ProfilePage.styled';
import usePrice from '@Hooks/usePrice';
import { _AddAddressToProfile, _GetProfileAddresses } from '@Services/Addresses';
import {
  _ApplyAddress,
  _ApplyPickupPoint,
  _DeSelectSameDayDelivery,
  _GetPickupCitiesList,
  _GetPickupPoint,
  _SelectSameDayDelivery,
} from '@Services/Checkout';
import { K_AddressesList, K_PickupCities, K_PickupPoints } from '@Utils/ReactQuery.keys';
import { AxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Pin from 'public/blue_address.svg';
import RedCheck from 'public/red-check.svg';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { UnmountClosed } from 'react-collapse';
import { UseFormSetError } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, RootState } from 'store';
import { AddressFormValues } from 'types/Address';
import AddressCollapsePickup from './AddressCollapsePickup';
import {
  MainCollapseAction,
  MainCollapseContent,
  MainCollapseFooter,
  MainCollapseHeader,
  MainCollapseWrapper,
  Step,
  SubCollapseCheck,
  SubCollapseContent,
  SubCollapseHeader,
  SubCollapseHeaderInfo,
  SubCollapseHeaderInner,
  SubCollapseIcon,
  SubCollapseTitle,
  SubCollapseWrapper,
  SubInfo,
} from './CheckoutSteps.styled';

type Props = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPaymentCollapseIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const formId = 'CHECKOUT_ADDRESS_FORM';
export default function AddressCollapse({ isOpen, setIsOpen, setPaymentCollapseIsOpen }: Props) {
  const { t } = useTranslation('Checkout');
  const { locale } = useRouter();
  const { refetch: refetchUserAddresses, data: userAddresses } = useQuery(
    K_AddressesList({
      locale,
    }),
    () => _GetProfileAddresses()
  );
  const { data } = useSelector((state: RootState) => state.checkout);
  const { checkout: checkoutModel } = useDispatch<Dispatch>();
  const { thePrice: shippingPrice } = usePrice(data?.total_price?.shipping_price || 0);
  // States of Address
  const isAddressEnabled = useMemo(
    () => Boolean(data?.checkout_items && data?.checkout_items.some((item) => !Boolean(item.need_pickup))),
    [data?.checkout_items]
  );
  const [addressCollapseIsOpen, setAddressCollapseIsOpen] = useState(isAddressEnabled && !Boolean(data?.address));
  const [initialAddressFormIsOpen, setInitialAddressFormIsOpen] = useState(isAddressEnabled);
  const [newAddressFormIsOpen, setNewAddressFormIsOpen] = useState(false);
  const [addressFormIsLoading, setAddressFormIsLodaing] = useState(false);
  // States of Pickup
  const isPickupEnabled = useMemo(
    () => Boolean(data?.checkout_items && data?.checkout_items.some((item) => Boolean(item.need_pickup))),
    [data?.checkout_items]
  );
  const { data: pickupCities } = useQuery(
    K_PickupCities({
      locale,
    }),
    () => _GetPickupCitiesList(),
    {
      enabled: isPickupEnabled,
    }
  );
  const [pickupDistrictId, setPickupDistrictId] = useState<number>();
  const { data: pickupPoints } = useQuery(
    K_PickupPoints({
      locale,
      district__id: pickupDistrictId,
    }),
    () =>
      _GetPickupPoint({
        district__id: pickupDistrictId,
      }),
    {
      enabled: isPickupEnabled,
    }
  );
  const [pickupCollapseIsOpen, setPickupCollapseIsOpen] = useState(isPickupEnabled && !isAddressEnabled);
  const [pickupIsLoading, setPickupIsLoading] = useState(false);

  const addressSubmitHandler = useCallback(
    ({ city_id, ...data }: AddressFormValues, setError: UseFormSetError<AddressFormValues>) => {
      setAddressFormIsLodaing(true);
      _AddAddressToProfile({
        ...data,
        district: data.district.value,
      })
        .then(async (data) => {
          const addressId = data.id;
          await refetchUserAddresses();
          const newCheckoutData = await _ApplyAddress(addressId);
          checkoutModel.setCheckoutData(newCheckoutData);
          setAddressFormIsLodaing(false);
          setInitialAddressFormIsOpen(false);
          if (newAddressFormIsOpen) {
            setNewAddressFormIsOpen(false);
          }
        })
        .catch((err: AxiosError) => {
          const errorData = err.response?.data;
          if (errorData) {
            const keys = Object.keys(errorData) as unknown as (keyof AddressFormValues)[];
            keys.map((key) =>
              setError(key, {
                type: 'manual',
                message: errorData[key][0],
              })
            );
          }
          setAddressFormIsLodaing(false);
        });
    },
    [checkoutModel, newAddressFormIsOpen, refetchUserAddresses]
  );

  const handleSelectAddress = useCallback(
    async (addressId) => {
      setAddressFormIsLodaing(true);
      const newCheckoutData = await _ApplyAddress(addressId);
      checkoutModel.setCheckoutData(newCheckoutData);
      setInitialAddressFormIsOpen(false);
      setAddressFormIsLodaing(false);
      if (newAddressFormIsOpen) {
        setNewAddressFormIsOpen(false);
      }
    },
    [checkoutModel, newAddressFormIsOpen]
  );

  const handleSameDayDelivery = useCallback(
    async (addSameDay: boolean) => {
      setAddressFormIsLodaing(true);
      const newCheckoutData = addSameDay ? await _SelectSameDayDelivery() : await _DeSelectSameDayDelivery();
      checkoutModel.setCheckoutData(newCheckoutData);
      setAddressFormIsLodaing(false);
    },
    [checkoutModel]
  );

  const handleSelectPickupPoint = useCallback(
    async (pickupPointId) => {
      setPickupIsLoading(true);
      const newCheckoutData = await _ApplyPickupPoint(pickupPointId);
      checkoutModel.setCheckoutData(newCheckoutData);
      setPickupIsLoading(false);
    },
    [checkoutModel]
  );

  const getNextBtnBehavior: () => React.ButtonHTMLAttributes<HTMLButtonElement> = () => {
    if (isAddressEnabled) {
      if (!Boolean(data?.address)) {
        if (
          (userAddresses?.results.length && newAddressFormIsOpen) ||
          (!userAddresses?.results.length && initialAddressFormIsOpen)
        ) {
          return {
            type: 'submit',
            form: formId,
          };
        } else {
          return {
            type: 'button',
            disabled: true,
          };
        }
      } else {
        if (newAddressFormIsOpen && addressCollapseIsOpen) {
          return {
            type: 'submit',
            form: formId,
          };
        }
        if (isPickupEnabled) {
          if (Boolean(data?.pick_point)) {
            return {
              type: 'button',
              onClick: () => {
                setAddressCollapseIsOpen(false);
                setPickupCollapseIsOpen(false);
                setIsOpen(false);
                setPaymentCollapseIsOpen(true);
              },
            };
          } else {
            if (pickupCollapseIsOpen) {
              return {
                type: 'button',
                disabled: true,
              };
            } else {
              return {
                type: 'button',
                onClick: () => {
                  setAddressCollapseIsOpen(false);
                  setPickupCollapseIsOpen(true);
                },
              };
            }
          }
        } else {
          return {
            type: 'button',
            onClick: () => {
              setAddressCollapseIsOpen(false);
              setPickupCollapseIsOpen(false);
              setIsOpen(false);
              setPaymentCollapseIsOpen(true);
            },
          };
        }
      }
    } else {
      if (Boolean(data?.pick_point)) {
        return {
          type: 'button',
          onClick: () => {
            setAddressCollapseIsOpen(false);
            setPickupCollapseIsOpen(false);
            setIsOpen(false);
            setPaymentCollapseIsOpen(true);
          },
        };
      } else {
        return {
          type: 'button',
          disabled: true,
        };
      }
    }
  };

  return (
    <MainCollapseWrapper isActive={isOpen}>
      <MainCollapseHeader
        active={isOpen}
        onClick={() => {
          setIsOpen((prev) => !prev);
          setPaymentCollapseIsOpen(false);
        }}
      >
        <Step>1</Step> {t('shipping')}
      </MainCollapseHeader>
      <UnmountClosed isOpened={isOpen}>
        {/* Content */}
        <MainCollapseContent>
          <SubCollapseWrapper>
            <SubCollapseHeader
              isActive={addressCollapseIsOpen}
              onClick={
                isAddressEnabled
                  ? () => {
                      setPickupCollapseIsOpen(false);
                      setAddressCollapseIsOpen((prev) => !prev);
                    }
                  : undefined
              }
              isDisabled={!isAddressEnabled}
            >
              <SubCollapseCheck />
              <SubCollapseHeaderInner>
                <SubCollapseIcon icon="deliver_to_home.svg" />
                <SubCollapseHeaderInfo>
                  {t('deliver_to_home')}
                  {Boolean(data?.total_price?.shipping_price) && (
                    <SubInfo>
                      {t('service_price')} {shippingPrice}
                    </SubInfo>
                  )}
                  {!Boolean(isAddressEnabled) && <SubInfo>{t('no-delivery-home')}</SubInfo>}
                </SubCollapseHeaderInfo>
              </SubCollapseHeaderInner>
            </SubCollapseHeader>
            <UnmountClosed isOpened={addressCollapseIsOpen}>
              <SubCollapseContent>
                <SubCollapseTitle>{t('select_address')}</SubCollapseTitle>
                {Boolean(userAddresses?.results.length) ? (
                  <Fragment>
                    {userAddresses?.results.map((address) => (
                      <AddressCard
                        key={address.id}
                        data={address}
                        inCheckout={{
                          isSelected: data?.address?.id === address.id,
                          onClick: data?.address?.id !== address.id ? () => handleSelectAddress(address.id) : undefined,
                          disabled: addressFormIsLoading,
                        }}
                      />
                    ))}
                    {Boolean(data?.address?.city.same_day_shipping_allowed) && (
                      <CheckBoxWrapper>
                        <CheckInput
                          onChange={() => handleSameDayDelivery(!Boolean(data?.shipping_in_same_day))}
                          name={'same_day_delivery'}
                          id={'same_day_delivery'}
                          type="checkbox"
                          checked={Boolean(data?.shipping_in_same_day)}
                          disabled={addressFormIsLoading}
                        />
                        <StyledCheckLabel htmlFor={'same_day_delivery'}>
                          {t('same-day-delivery')}
                          <Image src={RedCheck} width={16} height={12} alt="check" />
                        </StyledCheckLabel>
                      </CheckBoxWrapper>
                    )}
                    <AddNew onClick={() => setNewAddressFormIsOpen((prev) => !prev)}>
                      <AddNewIcon>
                        <Image src={Pin} width={30} height={29.03} alt="add car" />
                      </AddNewIcon>
                      {t('add-new-address')}
                    </AddNew>
                    <UnmountClosed isOpened={newAddressFormIsOpen}>
                      <AddressForm
                        submitHandler={addressSubmitHandler}
                        formId={formId}
                        isLoading={addressFormIsLoading}
                      />
                    </UnmountClosed>
                  </Fragment>
                ) : (
                  <AddressForm submitHandler={addressSubmitHandler} formId={formId} isLoading={addressFormIsLoading} />
                )}
              </SubCollapseContent>
            </UnmountClosed>
          </SubCollapseWrapper>
          <SubCollapseWrapper>
            <SubCollapseHeader
              isActive={pickupCollapseIsOpen}
              isDisabled={!isPickupEnabled}
              onClick={
                isPickupEnabled && isAddressEnabled && Boolean(data?.address)
                  ? () => {
                      setAddressCollapseIsOpen(false);
                      setPickupCollapseIsOpen((prev) => !prev);
                    }
                  : undefined
              }
            >
              <SubCollapseCheck />
              {/* <SubCollapseHeaderInner>
                <SubCollapseIcon icon="garage.svg" />
                <SubCollapseHeaderInfo>
                  {t('receive')}
                  <SubInfo>{t('choose_branch')}</SubInfo>
                </SubCollapseHeaderInfo>
              </SubCollapseHeaderInner> */}
            </SubCollapseHeader>
            <UnmountClosed isOpened={pickupCollapseIsOpen}>
              <SubCollapseContent>
                <AddressCollapsePickup
                  pickupCities={pickupCities?.results}
                  pickupPoints={pickupPoints?.results}
                  setPickupDistrictId={setPickupDistrictId}
                  handleSelectPickupPoint={handleSelectPickupPoint}
                  pickupIsLoading={pickupIsLoading}
                />
              </SubCollapseContent>
            </UnmountClosed>
          </SubCollapseWrapper>
          {/* Footer */}
          <MainCollapseFooter>
            <MainCollapseAction variant="filled-red" {...getNextBtnBehavior()}>
              {t('next')}
            </MainCollapseAction>
          </MainCollapseFooter>
        </MainCollapseContent>
      </UnmountClosed>
    </MainCollapseWrapper>
  );
}
